import type { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { hasVariants, isPartOfVariantGroup } from 'components/shared/variants/variant-grouping.js'
import type { SimpleWidgetCfg, WidgetCondition, WidgetConditionCfg } from './base-product-tile-types.js'

// Whether the product can be added to the basket. Tiles show their order form only through
// showWhen (orderable is true), so this is the single check for it.
export const isOrderable = (product: InternalProduct): boolean =>
  !product.ordering.cannotBeAddedToBasket && product.status.canOrderProduct !== false

const CONDITION_EVALUATORS: Record<WidgetCondition, (product: InternalProduct) => boolean> = {
  variant_grouping: product => isPartOfVariantGroup(product.numVariants, product.variant?.type),
  has_variants: hasVariants,
  is_configurator_product: product => product.configurator != null && product.configurator.id != null && product.configurator.url != null,
  orderable: isOrderable,
}

export const WIDGET_CONDITIONS = Object.keys(CONDITION_EVALUATORS) as WidgetCondition[]

const warnedInvalidConditions = new Set<string>()

const warnInvalidCondition = (raw: unknown) => {
  const key = JSON.stringify(raw)
  if (warnedInvalidConditions.has(key)) return
  warnedInvalidConditions.add(key)
  console.warn(
    `csc-base-product-tile: ignoring invalid showWhen condition ${key} in tile-config — the widget renders as if that condition wasn't there. ` +
    `A condition needs a "condition" (${WIDGET_CONDITIONS.join(', ')}) and "is" (true or false).`
  )
}

export function normalizeWidgetCondition(raw: unknown): WidgetConditionCfg | undefined {
  if (raw == null) return undefined
  const candidate = raw as Partial<Record<keyof WidgetConditionCfg, unknown>>
  const condition = WIDGET_CONDITIONS.find(c => c === candidate.condition)
  if (typeof raw !== 'object' || !condition || typeof candidate.is !== 'boolean') {
    warnInvalidCondition(raw)
    return undefined
  }
  return { condition, is: candidate.is }
}

export function normalizeShowWhen(widget: Pick<SimpleWidgetCfg, 'showWhen'>): WidgetConditionCfg[] {
  const raw: unknown[] = Array.isArray(widget.showWhen) ? widget.showWhen : []
  return raw.map(normalizeWidgetCondition).filter((condition): condition is WidgetConditionCfg => condition !== undefined)
}

export function isWidgetVisible(widget: Pick<SimpleWidgetCfg, 'showWhen'>, product: InternalProduct): boolean {
  return normalizeShowWhen(widget).every(({ condition, is }) => CONDITION_EVALUATORS[condition](product) === is)
}
